// The phone-width `/hr` overview — a deliberately lightweight landing, NOT a
// port of the dense desktop `HrDashboard`. See `routes/HrOverview.tsx` for
// why the two are different views rather than one responsive one.
//
// Shows the four case-bucket counts plus the **unacknowledged flag feed**,
// via the same `/api/hr/dashboard` endpoint. What it drops is the per-manager
// load view and the three privacy-gated aggregate tiles — analysis surfaces
// that want a big screen.
//
// The flag feed is NOT optional here, and an earlier revision that omitted it
// was a real regression: acknowledging a flag is a time-bound safety duty (a
// red flag must be reviewed within 24h), and no tab, no `HrMore` entry and no
// case page exposes the acknowledge action, so leaving it out made it
// unreachable at phone width — something that worked before, when `/hr`
// rendered the full dashboard at every width. It renders the same `FlagFeed`
// component the desktop dashboard does, so the action can't drift.
//
// Padding is the shell's job, so this adds none of its own.

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api.ts';
import { useSession } from '@/lib/auth.ts';
import { firstNameFromSession } from '@/lib/format.ts';
import { MaeveGreetingHero } from '@/components/MaeveGreetingHero.tsx';
import { FlagFeed, type HrFlagRow } from '@/routes/HrDashboard.tsx';

interface HrCompactDashboard {
  buckets: { onTrack: number; dueSoon: number; overdue: number; paused: number };
  flags: HrFlagRow[];
}

const TILES: { key: keyof HrCompactDashboard['buckets']; label: string }[] = [
  { key: 'onTrack', label: 'On track' },
  { key: 'dueSoon', label: 'Due soon' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'paused', label: 'Paused' },
];

export function HrCompactOverview() {
  const session = useSession('hr-shell');
  const firstName = firstNameFromSession(session as { name?: string });
  const dashboard = useQuery({
    queryKey: ['hr', 'dashboard'],
    queryFn: () => api.get<HrCompactDashboard>('/api/hr/dashboard'),
  });

  return (
    <div className="space-y-6">
      <MaeveGreetingHero
        eyebrow="HR"
        title={
          firstName
            ? `Hi ${firstName}, here's where things stand.`
            : 'Your organisation, at a glance.'
        }
        subtitle="Support pathway buckets below. For the full oversight view — flags, per-manager load, wellbeing trends — use besideU on a larger screen."
      />

      {dashboard.isPending ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : dashboard.error ? (
        <p className="text-traffic-red">Could not load your dashboard.</p>
      ) : (
        <ul aria-label="Support pathway buckets" className="grid grid-cols-2 gap-3">
          {TILES.map((tile) => (
            <li key={tile.key} className="rounded-lg border border-border bg-card p-4">
              <p className="text-2xl font-semibold tabular-nums">
                {dashboard.data.buckets[tile.key]}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{tile.label}</p>
            </li>
          ))}
        </ul>
      )}

      {dashboard.data ? <FlagFeed flags={dashboard.data.flags} /> : null}
    </div>
  );
}
